import { TRequester } from './VisitRequest';
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { initVisitorData, TDigitalDevices, TVisitor, TVisitorDigitalDevices } from './VisitRequest';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { DevicesOther } from '@mui/icons-material';
import { formatCompanyPhoneNumber, formatPhoneNumber, isValidCompanyPhoneNumber, isValidPhoneNumber } from 'constants/regularExpression';
import { useSnackbarStore } from 'store/useSnackbarStore';
import { useTranslation } from 'react-i18next';
import './VisitPhonePopup.css';

interface VisitPhonePopupProps {
  requester?: TRequester;
  updatePhoneNumber: (phoneNumber: any) => void;
  open: boolean;
  onClose: () => void;
}

const VisitPhonePopup: React.FC<VisitPhonePopupProps> = ({ requester, open, updatePhoneNumber, onClose }) => {
  const [phoneNumber, setPhoneNumber] = useState<string>(''); // 전화번호 에러 상태
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
  // 다크 라이트 모드
  const theme = useTheme();
  // 다국어
  const { t } = useTranslation();

  // visitor prop이 변경될 때마다 상태 초기화
  useEffect(() => {
    if (requester) {
      setPhoneNumber(requester.phoneNumber);
    } else {
      // 추가하는 경우 빈 초기값으로 설정
      setPhoneNumber('');
    }
  }, [requester]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'phoneNumber') {
      const formattedValue = formatPhoneNumber(value);
      setPhoneNumber(formattedValue);
    }
  };

  const requiredFields = [{ key: 'phoneNumber', label: t('visitor_info.phone') }];

  const handleCloseAndReset = () => {
    //setVisitorDevices([]); //디지털기기 초기화
    onClose(); // 팝업 닫기
  };

  const handleSaveOrEdit = async (isEdit: boolean) => {
    if (isValidPhoneNumber(phoneNumber)) {
      updatePhoneNumber(phoneNumber);
      onClose(); // 팝업 닫기
    } else {
      showSnackbar(`${t('visit_request.error_messages.request_change_warn')}`, 'warning');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '400px',
          maxWidth: '400px',
          maxHeight: '600px',
          margin: 'auto'
        }
      }}
    >
      <DialogTitle className="headerTitle" sx={{ bgcolor: theme.palette.table.header, border: 'none' }}>
        {/* 요청자 휴대폰 번호 수정 */}
        <span>
          {t('visit_request.applicant_information_list.phone_number')} {t('visit_request.visitor_information_list.button_name.edit')}
        </span>

        {/* 엑스 버튼 오른쪽에 배치 */}
        <IconButton onClick={handleCloseAndReset} sx={{ marginLeft: 'auto' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: theme.palette.mode == 'dark' ? '#4c4c4c' : '#ffffff', paddingTop: '20px !important', paddingBottom: '2px' }}>
        <Box display="flex" flexDirection="column" gap={1}>
          {/* 휴대폰번호 */}
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
              {t('visit_request.applicant_information_list.phone_number')}
              <FormLabel required sx={{ fontSize: '0.75rem' }} />
            </Typography>
            <TextField
              required
              name="phoneNumber"
              value={phoneNumber || ''}
              size="small"
              placeholder={t('visit_request.applicant_information_list.phone_number')}
              onChange={handleChange}
              sx={{ flexGrow: 1 }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '20px 24px', bgcolor: theme.palette.mode == 'dark' ? '#4c4c4c' : '#ffffff' }}>
        <Button
          variant="contained"
          color="primary"
          size="medium"
          fullWidth
          onClick={() => handleSaveOrEdit(true)}
          sx={{ borderRadius: '4px', fontWeight: 'bold' }}
        >
          {t('visitor_info.btn.edit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VisitPhonePopup;
